document.addEventListener('DOMContentLoaded', async function () {
    let sessionToken = null;
    let cardBrands = {};
    let sessionConfig = {
        flowType: 'tokenization',
        advancePayment: null,
        paymentDetails: null,
    };

    // PCI DSS Compliance: Detect environment (development vs production)
    // Consider development if: localhost, 127.0.0.1, 0.0.0.0, .local domains, or explicit NODE_ENV
    const isDevelopment = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1' ||
                         window.location.hostname === '0.0.0.0' ||
                         window.location.hostname.includes('.local') ||
                         window.location.hostname === '' || // Empty hostname (file:// protocol)
                         (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development');

    const showError = (message) => {
        const errorElement = document.createElement('div');
        errorElement.style.position = 'fixed';
        errorElement.style.top = '0';
        errorElement.style.left = '0';
        errorElement.style.width = '100%';
        errorElement.style.height = '100%';
        errorElement.style.display = 'flex';
        errorElement.style.textAlign = 'center';
        errorElement.style.alignItems = 'center';
        errorElement.style.justifyContent = 'center';
        errorElement.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
        errorElement.style.color = '#fff';
        errorElement.style.zIndex = '9999';
        errorElement.innerHTML = `<div><h2>Error</h2><p>${message}</p></div>`;
        document.body.appendChild(errorElement);
    };

    // PCI DSS Compliance: Secure logging system that prevents sensitive data exposure
    const secureLog = {
        log: function(...args) {
            if (isDevelopment) {
                console.log(...args);
            }
        },
        error: function(...args) {
            // Only log error messages, never sensitive data
            const sanitizedArgs = args.map(arg => {
                if (typeof arg === 'object' && arg !== null) {
                    // Remove sensitive fields from objects
                    const sanitized = { ...arg };
                    delete sanitized.card_number;
                    delete sanitized.cvv;
                    delete sanitized.cardNumber;
                    delete sanitized.cardData;
                    delete sanitized.token;
                    delete sanitized.session_token;
                    return sanitized;
                }
                return arg;
            });
            if (isDevelopment) {
                console.error(...sanitizedArgs);
            }
        },
        warn: function(...args) {
            if (isDevelopment) {
                console.warn(...args);
            }
        }
    };

    // PCI DSS Compliance: Validate HTTPS in production
    const validateSecureConnection = () => {
        if (!isDevelopment && window.location.protocol !== 'https:') {
            showError('Esta aplicación requiere una conexión segura (HTTPS).');
            return false;
        }
        return true;
    };

    // PCI DSS Compliance: Get parent origin securely
    const getParentOrigin = () => {
        try {
            // Try to get parent origin, fallback to current origin if not in iframe
            if (window.parent && window.parent !== window) {
                return document.referrer ? new URL(document.referrer).origin : window.location.origin;
            }
            return window.location.origin;
        } catch (e) {
            // Cross-origin iframe, use referrer or current origin
            return document.referrer ? new URL(document.referrer).origin : window.location.origin;
        }
    };

    // PCI DSS Compliance: Secure postMessage with origin validation
    const securePostMessage = (action, data) => {
        const targetOrigin = getParentOrigin();
        // Never send sensitive data in postMessage
        const sanitizedData = { ...data };
        if (sanitizedData.cardData) {
            delete sanitizedData.cardData;
        }
        if (sanitizedData.card_number) {
            delete sanitizedData.card_number;
        }
        if (sanitizedData.cvv) {
            delete sanitizedData.cvv;
        }
        window.parent.postMessage({ action, data: sanitizedData }, targetOrigin);
    };

    // PCI DSS Compliance: Clean sensitive data from memory
    const clearSensitiveData = (obj) => {
        if (obj && typeof obj === 'object') {
            const sensitiveKeys = ['card_number', 'cvv', 'cardNumber', 'cardData', 'token', 'session_token', 
                                  'encrypted_card_number', 'encrypted_cvv', 'pan', 'primary_account_number'];
            Object.keys(obj).forEach(key => {
                const lowerKey = key.toLowerCase();
                if (sensitiveKeys.includes(key) || 
                    lowerKey.includes('card') || 
                    lowerKey.includes('cvv') || 
                    (lowerKey.includes('number') && lowerKey.includes('card')) || 
                    lowerKey.includes('token')) {
                    // Overwrite with null, then delete
                    obj[key] = null;
                    delete obj[key];
                } else if (typeof obj[key] === 'object' && obj[key] !== null) {
                    clearSensitiveData(obj[key]);
                }
            });
        }
    };

    const getTokenFromUrl = () => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('token') || null;
    };

    const validateSession = async () => {
        try {
            const apiBaseUrl = process.env.API_URL || 'http://localhost:8016';
            const response = await fetch(`${apiBaseUrl}/api/sessions/validate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token: sessionToken }),
            });

            const result = await response.json();

            if (!result.success) {
                throw new Error(result.message || 'Token se sesión inválido.');
            }
            cardBrands = result.cardBrands;
            sessionConfig = {
                flowType: result.flow_type || 'tokenization',
                advancePayment: result.advance_payment || null,
                paymentDetails: result.payment_details || null,
            };
        } catch (error) {
            secureLog.error('Error validating session');
            showError(error.message || 'La sesión no es válida. Recargá la página e intentá de nuevo.');
            throw error; // Detener ejecución si la validación falla
        }
    };

    // PCI DSS Compliance: Validate secure connection before proceeding
    if (!validateSecureConnection()) {
        return;
    }

    // Capturar el token desde la URL
    sessionToken = getTokenFromUrl();

    if (!sessionToken) {
        showError('Session token is missing. Please provide a valid session.');
        return; // Detener ejecución si no hay token
    }

    try {
        // Validar la sesión antes de continuar
        await validateSession();

        // Inicializar el widget aquí si la sesión es válida
        initializeWidget(sessionToken, cardBrands, sessionConfig, secureLog, securePostMessage, clearSensitiveData);
    } catch (error) {
        secureLog.error('Widget initialization failed');
    }
});

const initializeWidget = (sessionToken, cardBrands, sessionConfig, secureLog, securePostMessage, clearSensitiveData) => {
    secureLog.log('Widget initialized');
    
    // PCI DSS Compliance: Ensure HTTPS in production
    // Use the same detection logic as the outer scope
    const isDevelopment = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1' ||
                         window.location.hostname === '0.0.0.0' ||
                         window.location.hostname.includes('.local') ||
                         window.location.hostname === '' || // Empty hostname (file:// protocol)
                         (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development');
    
    // Get API URL from environment or use current origin
    let apiBaseUrl;
    if (typeof process !== 'undefined' && process.env && process.env.API_URL) {
        apiBaseUrl = process.env.API_URL;
    } else {
        apiBaseUrl = isDevelopment ? 'http://localhost:8016' : window.location.origin;
    }
    
    // Validate API URL uses HTTPS in production
    if (!isDevelopment && apiBaseUrl.startsWith('http://')) {
        secureLog.error('API URL must use HTTPS in production');
        showError('Error de configuración: se requiere HTTPS.');
        return;
    }

    // Card elements
    const card = document.getElementById('card');
    const cardNumberDisplay = document.getElementById('card-number-display');
    const cardHolderDisplay = document.getElementById('card-holder-display');
    const cardExpiryDisplay = document.getElementById('card-expiry-display');
    const cvvDisplay = document.getElementById('cvv-display');
    const bankLogo = document.getElementById('bank-logo');
    const cardBrand = document.getElementById('card-brand');

    // Form elements
    const cardNumberInput = document.getElementById('card-number-input');
    const cardHolderInput = document.getElementById('card-holder-input');
    const expiryDateInput = document.getElementById('expiry-date-input');
    const cvvInput = document.getElementById('cvv-input');
    const documentNumberInput = document.getElementById('document-number-input');
    const submitButton = document.getElementById('submit-button');
    const cardForm = document.getElementById('card-form');
    const validationHint = document.getElementById('validation-hint');
    const initialLoadingScreen = document.getElementById('widget-loading-screen');
    const paymentIntro = document.getElementById('payment-intro');
    const paymentIntroAmount = document.getElementById('payment-intro-amount');
    const paymentIntroContinue = document.getElementById('payment-intro-continue');
    const paymentHeaderBack = document.getElementById('payment-header-back');
    const paymentProcessing = document.getElementById('payment-processing');
    const paymentProcessingAmount = document.getElementById('payment-processing-amount');
    const paymentProcessingSteps = Array.from(document.querySelectorAll('.payment-processing__step'));
    const successTitle = document.getElementById('success-title');
    const titleElement = document.getElementById('widget-title');

    const isPaymentFlow = sessionConfig.flowType === 'payment';
    const paymentAmount = Number(sessionConfig.advancePayment?.amount || 0);
    const paymentConcept = sessionConfig.paymentDetails?.concept || sessionConfig.paymentDetails?.description || null;
    const loadingSteps = [
        'Validando tu tarjeta',
        'Guardando tu tarjeta',
        'Conectando con el banco',
        'Procesando el pago'
    ];

    let paymentLoadingInterval = null;
    let paymentLoadingStepIndex = 0;
    const fieldInputs = [
        cardNumberInput,
        cardHolderInput,
        documentNumberInput,
        expiryDateInput,
        cvvInput,
    ];

    const hideInitialLoading = () => {
        document.body.classList.remove('widget-preparing');
        if (initialLoadingScreen) {
            initialLoadingScreen.classList.add('d-none');
        }
    };

    // Validation patterns
    const cardPatterns = {
        cardNumber: /^[0-9\s]{19}$/,  // 16 digits + 3 spaces
        cardHolder: /^[A-Z\s]{2,30}$/,
        expiryDate: /^(0[1-9]|1[0-2])\/\d{2}$/,
        cvv: /^[0-9]{3,4}$/,
        documentNumber: /^[0-9]{7,11}$/  // 7 to 11 numeric digits
    };

    let lastFirstDigit = '';
    let lastBin = '';
    let lastKeyWasDelete = false;

    // PCI DSS Compliance: Dispatch event with secure postMessage
    securePostMessage('cp:widget_start', {});

    expiryDateInput.addEventListener('keydown', function(event) {
        lastKeyWasDelete = event.key === 'Backspace' || event.key === 'Delete';
    });

    const formatCurrency = (amount) => {
        const normalizedAmount = Number(amount || 0);

        return new Intl.NumberFormat('es-AR', {
            style: 'currency',
            currency: 'ARS',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(normalizedAmount);
    };

    const setActivePaymentStep = (stepIndex) => {
        paymentProcessingSteps.forEach((stepElement, index) => {
            stepElement.classList.remove('is-active', 'is-complete');

            if (index < stepIndex) {
                stepElement.classList.add('is-complete');
            } else if (index === stepIndex) {
                stepElement.classList.add('is-active');
            }
        });
    };

    const stopPaymentLoading = () => {
        if (paymentLoadingInterval) {
            window.clearInterval(paymentLoadingInterval);
            paymentLoadingInterval = null;
        }
    };

    const updateValidationHint = () => {
        const hasInvalidFields = fieldInputs.some((input) => input.classList.contains('is-invalid'));
        validationHint.classList.toggle('d-none', !hasInvalidFields);
        document.body.classList.toggle('compact-errors-active', hasInvalidFields);
    };

    const setPaymentView = (view) => {
        if (!isPaymentFlow) {
            return;
        }

        paymentIntro.classList.toggle('d-none', view !== 'intro');
        paymentHeaderBack.classList.toggle('is-hidden', view !== 'form');
        document.getElementById('card-display').classList.toggle('d-none', view !== 'form');
        cardForm.classList.toggle('d-none', view !== 'form');
        paymentProcessing.classList.toggle('d-none', view !== 'processing');

        if (titleElement) {
            if (view === 'intro') {
                titleElement.textContent = 'Confirmá el pago inicial';
            } else if (view === 'form') {
                titleElement.textContent = 'Ingresá los datos de tu tarjeta';
            } else if (view === 'processing') {
                titleElement.textContent = 'Procesando tu pago...';
            }
        }
    };

    const showPaymentIntro = () => {
        stopPaymentLoading();
        validationHint.classList.add('d-none');
        setPaymentView('intro');
    };

    const showPaymentForm = () => {
        stopPaymentLoading();
        setPaymentView('form');
        updateValidationHint();
    };

    const startPaymentLoading = () => {
        stopPaymentLoading();
        paymentLoadingStepIndex = 0;
        setActivePaymentStep(paymentLoadingStepIndex);
        validationHint.classList.add('d-none');
        setPaymentView('processing');

        paymentLoadingInterval = window.setInterval(() => {
            if (paymentLoadingStepIndex < loadingSteps.length - 1) {
                paymentLoadingStepIndex += 1;
                setActivePaymentStep(paymentLoadingStepIndex);
            }
        }, 1800);
    };

    const resetPaymentLoading = () => {
        stopPaymentLoading();
        paymentLoadingStepIndex = 0;
        setActivePaymentStep(paymentLoadingStepIndex);
        showPaymentForm();
    };

    const configureFlowExperience = () => {
        if (!isPaymentFlow) {
            if (submitButton) {
                submitButton.textContent = 'Vinculá tu tarjeta';
            }
            if (successTitle) {
                successTitle.innerHTML = '<strong>¡Tarjeta vinculada!</strong>';
            }
            if (titleElement) {
                titleElement.textContent = 'Ingresá los datos de tu tarjeta';
            }
            hideInitialLoading();
            return;
        }

        const formattedAmount = formatCurrency(paymentAmount);

        paymentIntroAmount.textContent = formattedAmount;
        paymentProcessingAmount.textContent = formattedAmount;
        submitButton.textContent = `Pagar ${formattedAmount}`;

        if (successTitle) {
            successTitle.innerHTML = '<strong>¡Pago registrado!</strong>';
        }

        hideInitialLoading();
        showPaymentIntro();
    };

    // Show success message
    const showSuccessMessage = () => {
        const successMessage = document.getElementById('success-message');
        successMessage.classList.add('show');
    };

    const closeWidget = () => {
        const widgetModal = document.getElementById('widget-modal');
        if (widgetModal) {
            widgetModal.style.display = 'none';
        }
    };

    // Animation for submit button
    const animateSubmitButton = (success) => {
        if (isPaymentFlow) {
            if (success) {
                stopPaymentLoading();
                setActivePaymentStep(loadingSteps.length);
                setTimeout(showSuccessMessage, 400);
            } else {
                resetPaymentLoading();
                submitButton.disabled = false;
            }
            return;
        }

        if (success) {
            submitButton.classList.remove('progress');
            submitButton.classList.add('completed');
            setTimeout(showSuccessMessage, 600); // Esperar que el botón se complete y luego mostrar el mensaje
        } else {
            submitButton.classList.remove('progress');
            submitButton.classList.add('error');
            setTimeout(() => {
                submitButton.classList.remove('error');
                submitButton.disabled = false;
            }, 1000);
        }
    };

    const showFeedbackMessage = (message) => {
        const errorModal = document.getElementById('error-modal');
        const errorModalMessage = document.getElementById('error-modal-message');
    
        // Si no hay mensaje, usar el mensaje por defecto
        const errorMessage = message || 'Ocurrió un error.';
        errorModalMessage.textContent = errorMessage;
    
        // PCI DSS Compliance: Dispatch event with secure postMessage (no sensitive data)
        securePostMessage('cp:tokenization_failure', {
            error: errorMessage
        });

        // Mostrar el modal
        errorModal.classList.add('show');
    };

    const hideFeedbackMessage = () => {
        const errorModal = document.getElementById('error-modal');
        errorModal.classList.remove('show');
    };

    // Close modal handlers
    const errorModalClose = document.getElementById('error-modal-close');
    const errorModalButton = document.getElementById('error-modal-button');
    const errorModalElement = document.getElementById('error-modal');
    
    if (errorModalClose) {
        errorModalClose.addEventListener('click', hideFeedbackMessage);
    }
    
    if (errorModalButton) {
        errorModalButton.addEventListener('click', hideFeedbackMessage);
    }

    // Close modal when clicking on overlay
    if (errorModalElement) {
        const errorModalOverlay = errorModalElement.querySelector('.error-modal__overlay');
        if (errorModalOverlay) {
            errorModalOverlay.addEventListener('click', hideFeedbackMessage);
        }
    }

    // Close modal with Escape key
    document.addEventListener('keydown', function(event) {
        if (event.key === 'Escape' && errorModalElement && errorModalElement.classList.contains('show')) {
            hideFeedbackMessage();
        }
    });

    if (paymentIntroContinue) {
        paymentIntroContinue.addEventListener('click', showPaymentForm);
    }

    if (paymentHeaderBack) {
        paymentHeaderBack.addEventListener('click', showPaymentIntro);
    }

    configureFlowExperience();

    // Format card number with spaces and dots
    const formatCardNumber = (number) => {
        let formatted = number.match(/.{1,4}/g)?.join(' ') || '';
        while (formatted.length < 19) {
            if ((formatted.length + 1) % 5 === 0) {
                formatted += ' ';
            } else {
                formatted += '•';
            }
        }
        return formatted;
    };

    // Format expiry date with dots
    const formatExpiryDate = (date) => {
        let formatted = date.length > 2 ? date.slice(0, 2) + '/' + date.slice(2) : date;
        while (formatted.length < 5) {
            if (formatted.length === 2) {
                formatted += '/';
            } else {
                formatted += '•';
            }
        }
        return formatted;
    };

    // Update submit button state
    const updateSubmitButtonState = () => {
        const isFormValid = 
            cardPatterns.cardNumber.test(cardNumberInput.value) &&
            cardPatterns.cardHolder.test(cardHolderInput.value) &&
            cardPatterns.expiryDate.test(expiryDateInput.value) &&
            cardPatterns.cvv.test(cvvInput.value) &&
            cardPatterns.documentNumber.test(documentNumberInput.value);
        
        submitButton.disabled = !isFormValid;
    };

    // Update card preview and button state
    const updateCardPreviewAndButtonState = () => {
        updateCardPreview();
        updateSubmitButtonState();
    };

    // Update card preview
    const updateCardPreview = () => {
        let cardNumber = cardNumberInput.value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
        let formattedCardNumber = formatCardNumber(cardNumber);
        cardNumberDisplay.textContent = formattedCardNumber;

        let cardHolder = cardHolderInput.value.toUpperCase();
        if (cardHolder.length > 20) {
            cardHolder = cardHolder.slice(0, 20) + '...';
        }
        cardHolderDisplay.textContent = cardHolder || 'NOMBRE DEL TITULAR';

        let expiryDate = expiryDateInput.value.replace(/[^0-9]/g, '');
        let formattedExpiryDate = formatExpiryDate(expiryDate);
        cardExpiryDisplay.textContent = formattedExpiryDate;

        cvvDisplay.textContent = cvvInput.value.padEnd(3, '•');
    };

    // Fetch BIN info
    const fetchBinInfo = (bin) => {
        return;
        fetch(`${apiBaseUrl}/api/bin-info/${bin}`)
            .then(response => response.json())
            .then(data => {
                if (data.success) {
                    updateCardInfo(data);
                } else {
                    secureLog.error('BIN information not found');
                }
            })
            .catch(error => {
                secureLog.error('Error fetching BIN information');
            });
    };

    // Update card info with BIN data
    const updateCardInfo = (data) => {
        if (data.bank) {
            bankLogo.innerHTML = `<img src="${data.bank.logo}" alt="${data.bank.name}">`;
        } else {
            bankLogo.innerHTML = ''; // Default or empty if not found
        }

        if (data.brand) {
            updateCardBrand(data.brand.logo);
        } else {
            cardBrand.innerHTML = ''; // Default or empty if not found
        }

        card.style.backgroundColor = data.color || 'gray'; // Default color if not found
    };

    // Determine card brand based on the first digit
    const determineCardBrand = (cardNumber) => {
        const firstDigit = cardNumber.charAt(0);
        if (firstDigit !== lastFirstDigit) {
            lastFirstDigit = firstDigit;
            if (cardBrands[firstDigit]) {
                updateCardBrand(cardBrands[firstDigit].logo);
            } else {
                cardBrand.innerHTML = ''; // Default or empty if not found
            }
        }
    };

    // Update card brand
    const updateCardBrand = (logo) => {
        cardBrand.innerHTML = `<img src="/img/brands/${logo}" alt="Card Brand" />`;
    };

    // PCI DSS Compliance: Prevent copy/paste on sensitive fields
    const preventCopyPaste = (element, allowPaste = false) => {
        // Prevent copy
        element.addEventListener('copy', (e) => {
            e.preventDefault();
            secureLog.warn('Copy prevented on sensitive field');
            return false;
        });
        
        // Prevent cut
        element.addEventListener('cut', (e) => {
            e.preventDefault();
            secureLog.warn('Cut prevented on sensitive field');
            return false;
        });
        
        // Handle paste - only allow if explicitly enabled and validate input
        if (!allowPaste) {
            element.addEventListener('paste', (e) => {
                e.preventDefault();
                secureLog.warn('Paste prevented on sensitive field');
                return false;
            });
        } else {
            // For fields that might need paste (like card number), validate the content
            element.addEventListener('paste', (e) => {
                e.preventDefault();
                const pastedText = (e.clipboardData || window.clipboardData).getData('text');
                // Only allow paste if it's numeric and within reasonable length
                if (/^\d+$/.test(pastedText) && pastedText.length <= 19) {
                    element.value = pastedText.slice(0, 19);
                    element.dispatchEvent(new Event('input', { bubbles: true }));
                }
                secureLog.warn('Paste validated and processed');
                return false;
            });
        }
        
        // Prevent context menu (right-click)
        element.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            return false;
        });
        
        // Prevent drag start
        element.addEventListener('dragstart', (e) => {
            e.preventDefault();
            return false;
        });
        
        // Prevent drop
        element.addEventListener('drop', (e) => {
            e.preventDefault();
            return false;
        });
        
        // Prevent select all (Ctrl+A / Cmd+A) on sensitive fields
        element.addEventListener('keydown', (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
                e.preventDefault();
                return false;
            }
        });
    };

    // Apply protection to sensitive fields
    // CVV and expiry date: no paste allowed (security)
    preventCopyPaste(cvvInput, false);
    preventCopyPaste(expiryDateInput, false);
    preventCopyPaste(documentNumberInput, false);
    
    // Card number: allow paste but validate (UX vs Security trade-off)
    // Note: For full PCI compliance, consider disabling paste here too
    preventCopyPaste(cardNumberInput, true);

    // PCI DSS Compliance: Format and validate card number input
    cardNumberInput.addEventListener('input', function() {
        let cardNumber = cardNumberInput.value.replace(/[^\d]/g, ''); // Remove non-digits
        
        // Determine card brand if first digit changed
        determineCardBrand(cardNumber);

        // Fetch BIN info if the first 6 digits changed
        if (cardNumber.length >= 6) {
            let bin = cardNumber.slice(0, 6);
            if (bin !== lastBin) {
                lastBin = bin;
                fetchBinInfo(bin);
            }
        }

        cardNumber = cardNumber.slice(0, 16); // Limit to 16 digits
        let formattedCardNumber = formatCardNumber(cardNumber); // Apply format with spaces and dots
        cardNumberInput.value = formattedCardNumber.replace(/•/g, '').trim(); // Remove dots from input
        // Remove validation classes during input (only format, don't show errors)
        cardNumberInput.classList.remove('is-invalid');
        updateValidationHint();
        updateCardPreviewAndButtonState();
    });

    // Validate card number on blur
    cardNumberInput.addEventListener('blur', function() {
        if (!cardPatterns.cardNumber.test(cardNumberInput.value)) {
            cardNumberInput.classList.add('is-invalid');
        } else {
            cardNumberInput.classList.remove('is-invalid');
        }
        updateValidationHint();
    });

    // Format and validate card holder input
    cardHolderInput.addEventListener('input', function() {
        cardHolderInput.value = cardHolderInput.value.replace(/[^a-zA-Z\s]/g, '').toUpperCase();
        // Remove validation classes during input (only format, don't show errors)
        cardHolderInput.classList.remove('is-invalid');
        updateValidationHint();
        updateCardPreviewAndButtonState();
    });

    // Validate card holder on blur
    cardHolderInput.addEventListener('blur', function() {
        if (!cardPatterns.cardHolder.test(cardHolderInput.value)) {
            cardHolderInput.classList.add('is-invalid');
        } else {
            cardHolderInput.classList.remove('is-invalid');
        }
        updateValidationHint();
    });

    // Format and validate expiry date input
    expiryDateInput.addEventListener('input', function() {
        let expiryDate = expiryDateInput.value.replace(/[^0-9]/g, '').slice(0, 4);
        
        if (expiryDate.length > 2 || (!lastKeyWasDelete && expiryDate.length == 2)) {
            expiryDate = expiryDate.slice(0, 2) + '/' + expiryDate.slice(2);
        }

        expiryDateInput.value = expiryDate;
        // Remove validation classes during input (only format, don't show errors)
        expiryDateInput.classList.remove('is-invalid');
        expiryDateInput.setCustomValidity('');
        updateValidationHint();

        updateCardPreviewAndButtonState();
    });

    // Validate expiry date on blur
    expiryDateInput.addEventListener('blur', function() {
        validateExpiryDate();
    });

    // Function to validate expiry date
    const validateExpiryDate = () => {
        const expiryDate = expiryDateInput.value;
        
        if (!cardPatterns.expiryDate.test(expiryDate)) {
            expiryDateInput.classList.add('is-invalid');
            expiryDateInput.setCustomValidity('Ingresá una fecha válida');
            updateValidationHint();
            return false;
        }

        if (expiryDate.length == 5) {
            const currentDate = new Date();
            const currentMonth = (currentDate.getMonth() + 1).toString().padStart(2, '0');
            const currentYear = currentDate.getFullYear().toString().slice(2);
    
            const enteredMonth = expiryDate.slice(0, 2);
            const enteredYear = expiryDate.slice(3);
    
            if (enteredMonth > 12 || enteredMonth <= 0 || enteredYear < currentYear || (enteredYear === currentYear && enteredMonth < currentMonth)) {
                expiryDateInput.setCustomValidity('La fecha de vencimiento no puede ser anterior al mes actual');
                expiryDateInput.classList.add('is-invalid');
                updateValidationHint();
                return false;
            }
        }

        expiryDateInput.classList.remove('is-invalid');
        expiryDateInput.setCustomValidity('');
        updateValidationHint();
        return true;
    };

    // Validate document number input
    documentNumberInput.addEventListener('input', function() {
        // Remove non-numeric characters and limit to 11 digits
        let documentNumber = documentNumberInput.value.replace(/[^0-9]/g, '').slice(0, 11);
        documentNumberInput.value = documentNumber;
        // Remove validation classes during input (only format, don't show errors)
        documentNumberInput.classList.remove('is-invalid');
        documentNumberInput.setCustomValidity('');
        updateValidationHint();
        updateCardPreviewAndButtonState();
    });

    // Validate document number on blur
    documentNumberInput.addEventListener('blur', function() {
        validateDocumentNumber();
    });

    // Function to validate document number
    const validateDocumentNumber = () => {
        const documentNumber = documentNumberInput.value.trim();
        
        if (documentNumber.length > 0 && documentNumber.length < 7) {
            documentNumberInput.classList.add('is-invalid');
            documentNumberInput.setCustomValidity('El número de documento debe tener entre 7 y 11 dígitos');
            updateValidationHint();
            return false;
        } else if (documentNumber.length >= 7 && documentNumber.length <= 11) {
            documentNumberInput.classList.remove('is-invalid');
            documentNumberInput.setCustomValidity('');
            updateValidationHint();
            return true;
        } else if (documentNumber.length === 0) {
            documentNumberInput.classList.remove('is-invalid');
            documentNumberInput.setCustomValidity('');
            updateValidationHint();
            return false;
        } else {
            documentNumberInput.classList.add('is-invalid');
            documentNumberInput.setCustomValidity('El número de documento debe tener entre 7 y 11 dígitos');
            updateValidationHint();
            return false;
        }
    };

    // Show card back on CVV focus
    cvvInput.addEventListener('focus', function() {
        document.getElementById('card-display').classList.add('flipped');
    });

    // Show card front on CVV blur
    cvvInput.addEventListener('blur', function() {
        document.getElementById('card-display').classList.remove('flipped');
    });

    // Format and validate CVV input
    cvvInput.addEventListener('input', function() {
        cvvInput.value = cvvInput.value.replace(/[^0-9]/g, '').slice(0, 4);
        // Remove validation classes during input (only format, don't show errors)
        cvvInput.classList.remove('is-invalid');
        updateValidationHint();
        updateCardPreviewAndButtonState();
    });

    // Validate CVV on blur
    cvvInput.addEventListener('blur', function() {
        if (!cardPatterns.cvv.test(cvvInput.value)) {
            cvvInput.classList.add('is-invalid');
        } else {
            cvvInput.classList.remove('is-invalid');
        }
        updateValidationHint();
    });

    const handleThreeDSResponse = (event) => {
        // Verificar origen del mensaje por seguridad
        const allowedOrigins = [
            'https://pay.checkout-hub.com',
            window.location.origin
        ];
        
        if (!allowedOrigins.includes(event.origin)) {
            secureLog.warn('Message received from unauthorized origin');
            return;
        }

        // Ignorar mensajes de react-devtools y otros mensajes no relacionados
        if (event.data && (
            event.data.source === 'react-devtools-content-script' ||
            event.data.source === 'react-devtools-bridge' ||
            event.data.source === 'react-devtools-backend'
        )) {
            return;
        }

        let response;
        try {
            // Intentar parsear como JSON primero
            response = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        } catch (e) {
            // Si no es JSON, usar el string directamente
            response = event.data;
        }

        // PCI DSS Compliance: Log only non-sensitive information
        secureLog.log('3DS Response received');

        // Solo procesar mensajes que contengan información relevante para 3DS
        if (typeof response === 'string' && (
            response.includes('challenge') || 
            response.includes('3ds') ||
            response.includes('verification')
        )) {
            // Remover el listener para evitar duplicados
            window.removeEventListener('message', handleThreeDSResponse);
            
            // Por ahora, asumimos que cualquier mensaje que no sea 'challenge.failed' es un éxito
            const isSuccess = response !== 'challenge.failed';

            // Restaurar el título original
            const titleElement = document.querySelector('.widget__steps__box__steps h3');
            if (titleElement) {
                titleElement.textContent = 'Ingresá los datos de tu tarjeta';
            }

            // Mostrar la tarjeta nuevamente
            const cardDisplay = document.getElementById('card-display');
            if (cardDisplay) {
                cardDisplay.style.display = 'block';
            }

            if (isSuccess) {
                // PCI DSS Compliance: Notify parent with secure postMessage
                securePostMessage('cp:tokenization_success', { 
                    status: 'success', 
                    message: 'Verificación 3DS exitosa' 
                });
                
                // Mostrar mensaje de éxito
                animateSubmitButton(true);
            } else {
                // PCI DSS Compliance: Notify parent with secure postMessage
                securePostMessage('cp:tokenization_failure', { 
                    error: 'La verificación 3DS ha fallado' 
                });
                
                // Mostrar error y volver al formulario
                const threeDSContainer = document.getElementById('threeds-container');
                if (threeDSContainer) {
                    threeDSContainer.remove();
                }
                
                document.getElementById('card-form').style.display = 'block';
                showFeedbackMessage('Tu banco no pudo confirmar la tarjeta. Intentá de nuevo.');
                animateSubmitButton(false);
            }
        }
    };

    // Función para escuchar mensajes del iframe de 3DS
    const listenFor3DSMessages = () => {
        window.addEventListener('message', handleThreeDSResponse);
    };

    const showThreeDSFrame = (redirectUrl) => {
        if (isPaymentFlow) {
            showPaymentForm();
        }

        // Ocultar el formulario y limpiar mensajes anteriores
        document.getElementById('card-form').style.display = 'none';
        hideFeedbackMessage();

        // Ocultar la tarjeta de preview
        const cardDisplay = document.getElementById('card-display');
        if (cardDisplay) {
            cardDisplay.style.display = 'none';
        }

        // Actualizar el título
        const titleElement = document.querySelector('.widget__steps__box__steps h3');
        if (titleElement) {
            titleElement.textContent = 'Confirmá con tu banco';
        }
        
        // Crear y mostrar el iframe para 3DS
        const threeDSContainer = document.createElement('div');
        threeDSContainer.id = 'threeds-container';
        threeDSContainer.style.width = '100%';
        threeDSContainer.style.height = '450px';
        threeDSContainer.style.position = 'relative';
        
        const iframe = document.createElement('iframe');
        iframe.src = redirectUrl;
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        iframe.style.border = 'none';
        iframe.setAttribute('allowfullscreen', 'true');
        iframe.setAttribute('allow', 'payment');
        
        threeDSContainer.appendChild(iframe);
        
        // Asegurarnos de que el contenedor del widget esté visible
        const widgetContainer = document.querySelector('.widget__steps__box-input');
        if (widgetContainer) {
            widgetContainer.innerHTML = ''; // Limpiar contenido anterior
            
            // Agregar mensaje explicativo antes del iframe
            const explanationMessage = document.createElement('div');
            explanationMessage.className = 'text-center mb-3';
            explanationMessage.innerHTML = '<p>Tu banco te va a pedir que confirmes la tarjeta</p>';
            widgetContainer.appendChild(explanationMessage);
            
            widgetContainer.appendChild(threeDSContainer);
            widgetContainer.style.display = 'block';
        }

        // Iniciar el listener para mensajes del iframe
        listenFor3DSMessages();
    };

    // Modificar el evento submit del formulario
    cardForm.addEventListener('submit', function(event) {
        event.preventDefault();
        let valid = true;

        if (!cardPatterns.cardNumber.test(cardNumberInput.value)) {
            cardNumberInput.classList.add('is-invalid');
            valid = false;
        } else {
            cardNumberInput.classList.remove('is-invalid');
        }

        if (!cardPatterns.cardHolder.test(cardHolderInput.value)) {
            cardHolderInput.classList.add('is-invalid');
            valid = false; 
        } else {
            cardHolderInput.classList.remove('is-invalid');
        }

        if (!validateExpiryDate()) {
            valid = false;
        }

        if (!cardPatterns.cvv.test(cvvInput.value)) {
            cvvInput.classList.add('is-invalid');
            valid = false;
        } else {
            cvvInput.classList.remove('is-invalid');
        }

        if (!validateDocumentNumber()) {
            valid = false;
        }

        updateValidationHint();

        if (valid) {
            validationHint.classList.add('d-none');
            // PCI DSS Compliance: Prepare card data securely
            const cardData = {
                card_number: cardNumberInput.value.replace(/\s+/g, ''),
                card_holder: cardHolderInput.value,
                expiry_date: expiryDateInput.value,
                cvv: cvvInput.value,
                document_number: documentNumberInput.value,
                session_token: sessionToken
            };

            submitButton.disabled = true;

            if (isPaymentFlow) {
                startPaymentLoading();
            } else {
                submitButton.classList.add('progress', 'progress-loading');
            }

            // PCI DSS Compliance: Clear input values from DOM after reading (reduces memory exposure)
            const cardNumberValue = cardData.card_number;
            const cvvValue = cardData.cvv;
            
            fetch(`${apiBaseUrl}/api/tokenize-card`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                },
                body: JSON.stringify(cardData)
            })
            .then(response => response.json())
            .then(data => {
                // PCI DSS Compliance: Clear sensitive data from memory immediately after use
                clearSensitiveData(cardData);
                cardData.card_number = null;
                cardData.cvv = null;
                
                if (data.success) {
                    if (data.requires_3ds) {
                        // Mostrar iframe para verificación 3DS
                        showThreeDSFrame(data.redirect_url);
                    } else {
                        // PCI DSS Compliance: Flujo normal sin 3DS - use secure postMessage
                        securePostMessage('cp:tokenization_success', data);
                        if (!isPaymentFlow) {
                            submitButton.style.transition = 'background-position 0.5s linear';
                            submitButton.classList.add('completed');
                        }
                        animateSubmitButton(true);
                        if (data.callback_url) {
                            setTimeout(() => { window.location.href = data.callback_url; }, 6000);
                        }
                    }
                } else {
                    if (!isPaymentFlow) {
                        submitButton.style.transition = 'background-position 0.5s linear';
                    }
                    animateSubmitButton(false);
                    secureLog.error('Tokenization failed');
                    showFeedbackMessage(data.message);
                }
            })
            .catch(error => {
                // PCI DSS Compliance: Clear sensitive data even on error
                clearSensitiveData(cardData);
                cardData.card_number = null;
                cardData.cvv = null;
                
                if (!isPaymentFlow) {
                    submitButton.style.transition = 'background-position 0.5s linear';
                }
                animateSubmitButton(false);
                showFeedbackMessage();
                secureLog.error('Error during tokenization');
            })
            .finally(() => {
                // PCI DSS Compliance: Additional cleanup - clear input values
                // Note: We don't clear the actual inputs as user might need to see them,
                // but we ensure the cardData object is cleaned
                cardData.card_number = null;
                cardData.cvv = null;
            });
        } else {
            const firstInvalidField = fieldInputs.find((input) => input.classList.contains('is-invalid'));
            if (firstInvalidField) {
                firstInvalidField.focus();
            }
        }
    });

    // Initialize submit button state
    updateSubmitButtonState();

};

